/**
 * Recovery for a tab that outlived a deployment.
 *
 * Each build gives its JavaScript chunks new hashed names. A tab opened before
 * a deployment still imports the OLD names when it lazy-loads a page. Releases
 * keep old chunks for a week (scripts/build-release.mjs), so this should be
 * rare — but when a chunk really is gone, the user must not see a raw
 * "Failed to fetch dynamically imported module" as if the site were down.
 *
 * Only chunk-loading failures are handled here; every other error still
 * reaches the normal error screen untouched.
 */

const RELOAD_KEY = 'flash:stale-chunk-reload-at';
/** A second failure this soon after our own reload means reloading did not help. */
const LOOP_WINDOW_MS = 2 * 60_000;

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i, // Chrome
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari
  /Unable to preload CSS/i, // Vite CSS preload
  /Loading (CSS )?chunk [\w-]+ failed/i,
  /ChunkLoadError/i,
];

export function isStaleChunkError(error: unknown): boolean {
  const text =
    typeof error === 'string'
      ? error
      : `${(error as any)?.name ?? ''} ${(error as any)?.message ?? ''}`;
  return CHUNK_ERROR_PATTERNS.some(pattern => pattern.test(text));
}

function readReloadAt(): number {
  try {
    return Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
  } catch {
    return 0;
  }
}

/** True when we already reloaded for a stale chunk a moment ago (loop guard). */
export function recentlyReloadedForStaleChunk(): boolean {
  return Date.now() - readReloadAt() < LOOP_WINDOW_MS;
}

/**
 * Is this a new deployment rather than a network problem? The browser cannot
 * load a chunk either because it was removed (stale — reload fixes it) or
 * because the connection dropped (reloading would only show an offline page).
 */
async function serverIsReachable(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
  try {
    const response = await fetch(`${window.location.origin}/manifest.json`, {
      method: 'HEAD',
      cache: 'no-store',
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Reload once to pick up the current build. Resolves `false` — without
 * reloading — when offline, or when we already reloaded moments ago.
 */
export async function reloadForStaleChunk(): Promise<boolean> {
  if (recentlyReloadedForStaleChunk()) return false;
  if (!(await serverIsReachable())) return false;
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Without storage there is no loop guard, so do not auto-reload at all.
    return false;
  }
  window.location.reload();
  return true;
}

/**
 * Catches chunk failures that happen outside a route (lazy components, preload
 * of a route's dependencies) and reloads once. Route-level failures are also
 * shown as a friendly screen by RootErrorElement.
 */
export function installStaleChunkRecovery() {
  window.addEventListener('vite:preloadError', event => {
    if (!recentlyReloadedForStaleChunk()) {
      // Let our reload replace the error; if we end up not reloading, the
      // import still fails normally and the error screen explains it.
      void reloadForStaleChunk();
    }
    void event;
  });
  window.addEventListener('unhandledrejection', event => {
    if (isStaleChunkError(event.reason)) {
      void reloadForStaleChunk();
    }
  });
}
